import React from 'react';
import { useGetEmailDetailsById } from '../../../../query/mail';
import { Spin, Steps } from 'antd';
import moment from 'moment';
import IFrame from '../../../../components/blocks/IFrame';
import * as cheerio from 'cheerio';

const { Step } = Steps;

type Props = {
  id: number,
};

const dateFormat = 'YYYY-MM-DD HH:mm';

function removeTrackingLinkFromRenderedHtml(html: string) {
  const $ = cheerio.load(html);
  // Get all a tags
  const aTags = $('a');
  // Loop through all a tags
  aTags.each((index, aTag) => {
    // Get href attribute
    const href = $(aTag).attr('href');
    const url = new URL(href);
    const searchParams = new URLSearchParams(url.search);
    const originalUrl = searchParams.get('rd');
    if (originalUrl) {
      // Replace href attribute
      $(aTag).attr('href', originalUrl);
    }
  });
  // Get open tracking image
  const images = $('img[src*="open"]');
  // Loop through all images
  images.each((index, image) => {
    // Get src attribute
    const src = $(image).attr('src');
    if (src.includes('/analytics/open')) {
      // Remove image
      $(image).remove();
    }
  });
  return $.html();
}

function EmailDetails(props: { email: any, mailBody: any }) {
  const renderRow = (title, content) => {
    return (
      <div className="flex items-center">
        <div style={{ width: 150, fontWeight: 'bold' }}>{title}</div>
        <div className="flex flex-1">{content}</div>
      </div>
    );
  };
  return (
    <div className="space-y-2" style={{ fontSize: 12 }}>
      {renderRow('Email ID', props.email.emailId)}
      {renderRow('From', props.email.sender)}
      {renderRow('To', props.email.receiver)}
      {renderRow('Sent Through', props.email.sentThrough)}
      {renderRow('Subject', props?.mailBody?.subject || 'N/A')}
    </div>
  );
}

function getStepPropsFromEvent(event) {
  switch (event.type) {
    case 'open':
      return {
        status: 'finish',
        title: 'Opened',
        description: moment(event.createdDate).format(dateFormat),
      };
    case 'click':
      return {
        status: 'finish',
        title: 'Clicked',
        description: (
          <>
            {moment(event.createdDate).format(dateFormat)}
            <br />
            URL:{' '}
            <a href={event.link} target="_blank">
              {event.link}
            </a>
          </>
        ),
      };
    case 'failure':
      return {
        status: 'error',
        title: 'Failure',
        description: (
          <>
            {moment(event.createdDate).format(dateFormat)}
            <br />
            URL: {event.reason}
          </>
        ),
      };
    case 'reply':
      return {
        status: 'finish',
        title: 'Replied',
        description: moment(event.createdDate).format(dateFormat),
      };
    default:
      return {};
  }
}

function EmailEventHistory(props: { email: any, events: any[] }) {
  const { email, events } = props;
  const steps = [
    {
      title: 'Sent',
      description: moment(email.createdDate).format(dateFormat),
    },
    ...(email.delivered
      ? [
          {
            title: 'Delivered',
            description: moment(email.createdDate).format(dateFormat),
            status: 'finish',
          },
        ]
      : []),
    ...events.map(event => getStepPropsFromEvent(event)),
  ];
  return (
    <Steps current={steps.length - 1} direction="vertical" progressDot>
      {steps.map((step, index) => (
        <Step key={index} {...step} />
      ))}
    </Steps>
  );
}

export default function ViewEmailModalDetails(props: Props) {
  const { data, isLoading } = useGetEmailDetailsById(props.id);
  return (
    <Spin spinning={isLoading}>
      <div className="flex space-x-2">
        <div className="flex flex-1 flex-col">
          <strong>Details</strong>
          {data && (
            <div style={{ marginLeft: 8, marginTop: 12 }}>
              <EmailDetails email={data.email} mailBody={data.mailBody} />
            </div>
          )}
          <strong style={{ marginTop: 36 }}>Event History</strong>
          {data && (
            <div className="w-full" style={{ marginLeft: 8, marginTop: 12 }}>
              <EmailEventHistory email={data.email} events={data.events} />
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <strong>Email Body</strong>
          {data && (
            <div
              className="flex flex-1"
              style={{ marginTop: 12, minHeight: 400 }}
            >
              {data.mailBody ? (
                <IFrame
                  html={removeTrackingLinkFromRenderedHtml(data.mailBody.html)}
                />
              ) : (
                'Unable to load email body'
              )}
            </div>
          )}
          {/*<IFrame html={} />*/}
        </div>
      </div>
    </Spin>
  );
}
